import { z } from 'zod';
import * as dt from '../data.types.js';
import {
  BaseResponseSchema,
  integerIdSchema,
} from './primitives.js';

// ============================================================================
// NEW: Tracked File Upload Schemas (with database tracking via FileService)
// ============================================================================

/**
 * File entity types for polymorphic file relationships
 */
export const fileEntityTypeSchema = z.nativeEnum(dt.FILE_ENTITY_TYPE);

/**
 * File categories for organization and filtering
 */
export const fileCategorySchema = z.nativeEnum(dt.FILE_CATEGORY);

/**
 * Request body for tracked file uploads (single or multiple files)
 * 
 * This schema is used as FormData body params alongside the file(s).
 * All uploads using this schema are tracked in the database.
 * 
 * Usage:
 * - entityType: REQUIRED - What type of entity the file belongs to
 * - entityId: OPTIONAL - Specific entity ID (if associating with existing entity)
 * - fileCategory: OPTIONAL - Defaults to 'image' for images, 'document' for others
 * 
 * Examples:
 * 1. Generic container image (no specific container yet):
 *    { entityType: dt.FILE_ENTITY_TYPE.Container, fileCategory: dt.FILE_CATEGORY.Image }
 * 
 * 2. Wafer photo for existing wafer #123:
 *    { entityType: dt.FILE_ENTITY_TYPE.Wafer, entityId: 123, fileCategory: dt.FILE_CATEGORY.Image }
 * 
 * 3. Wafer fingerprint:
 *    { entityType: 'wafer', entityId: 123, fileCategory: 'fingerprint_image' }
 */
export const TrackedFileUploadBodySchema = z.object({
  entityType: fileEntityTypeSchema,
  entityId: integerIdSchema.optional(),
  fileCategory: fileCategorySchema.optional(),
  tags: z.array(z.string().max(50)).max(10).optional(),
  expiresInDays: z.coerce.number().int().min(1).max(365).optional(),
  /** Browser filenames, parallel to multipart `files` (used for user-facing errors). */
  clientOriginalNames: z
    .preprocess((val) => {
      if (val === undefined || val === null || val === '') {
        return undefined;
      }
      return Array.isArray(val) ? val : [val];
    }, z.array(z.string().max(255).trim()).max(20))
    .optional(),
});

function isBrowserFile(value: unknown): boolean {
  const FileCtor = (globalThis as { File?: new (...args: unknown[]) => object }).File;
  return typeof FileCtor === 'function' && value instanceof FileCtor;
}

/** FormData body for POST /tenant/upload/files. */
export const TrackedMultipleFileUploadFormSchema = TrackedFileUploadBodySchema.extend({
  files: z.array(z.custom(isBrowserFile)).min(1).max(20),
});

/** FormData body for POST /tenant/upload/single. */
export const TrackedSingleFileUploadFormSchema = TrackedFileUploadBodySchema.extend({
  file: z.custom(isBrowserFile),
});

/**
 * Response for tracked file upload (with database record info)
 */
export const TrackedSingleFileUploadResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    fileId: integerIdSchema,
    objectKey: z.string(),
    originalName: z.string(),
    mimeType: z.string(),
    size: z.number().int(),
    entityType: fileEntityTypeSchema,
    entityId: integerIdSchema.nullable(),
    fileCategory: fileCategorySchema
  })
});

/**
 * Response for tracked multiple file upload
 */
const TrackedUploadedFileEntrySchema = z.object({
  fileId: integerIdSchema,
  objectKey: z.string(),
  originalName: z.string(),
  mimeType: z.string(),
  size: z.number().int(),
  entityType: fileEntityTypeSchema,
  entityId: integerIdSchema.nullable(),
  fileCategory: fileCategorySchema,
});

export const TrackedFileUploadFailureSchema = z.object({
  originalName: z.string(),
  /** User-facing filename (from client); falls back to originalName when omitted. */
  displayName: z.string().optional(),
  error: z.string(),
});

export const TrackedMultipleFileUploadResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    uploadedFiles: z.array(TrackedUploadedFileEntrySchema),
    totalFiles: z.number().int(),
    failedUploads: z.array(TrackedFileUploadFailureSchema).optional(),
  }),
});
// Inferred types
export type FileCategory = z.infer<typeof fileCategorySchema>;
export type FileEntityType = z.infer<typeof fileEntityTypeSchema>;
export type TrackedFileUploadBody = z.infer<typeof TrackedFileUploadBodySchema>;
export type TrackedFileUploadFailure = z.infer<typeof TrackedFileUploadFailureSchema>;
export type TrackedMultipleFileUploadForm = z.infer<typeof TrackedMultipleFileUploadFormSchema>;
export type TrackedMultipleFileUploadResponse = z.infer<typeof TrackedMultipleFileUploadResponseSchema>;
export type TrackedSingleFileUploadForm = z.infer<typeof TrackedSingleFileUploadFormSchema>;
export type TrackedSingleFileUploadResponse = z.infer<typeof TrackedSingleFileUploadResponseSchema>;
